import React, { useState, useMemo, type PropsWithChildren } from 'react';
import { prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useAuthSelector, useUserSelector, useDeviceSelector } from 'js/store/hooks';

import * as Types from './types';
import * as helpers from './helpers';
import { ComponentPropsContext, ComponentConfigContext, ComponentMethodsContext } from './context';

import style from './withComponent.scss';

export const withComponent =
    ({ isForLoggedIn = false, isForLoggedOut = false, parseConfig = (x) => x, tkPrefix }: Types.IWithComponentOptions = {}) =>
    <P extends object>(WrappedComponent: React.ComponentType<P>) =>
    (props: P & Types.IWithComponentPortletProps) => {
        const {
            config,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            children,
            setShowWrapper,
            portletTkPrefix,
            portletPreferences,
            setWrapperClassNames,
            ...otherProps
        } = props as P & PropsWithChildren<Types.IWithComponentPortletProps>;
        const isComponentMounted = useIsMounted();
        const isLoggedIn = useUserSelector(prop('isLoggedIn'));
        const formFactor = useDeviceSelector(prop('formFactor'));
        const [componentError, setComponentError] = useState<string | null>(null);
        const [componentMessage, setComponentMessage] = useState<string | null>(null);
        const translate = useTranslations('playtech.mobile.system.component.');
        const isLoginInProcess = useAuthSelector(prop('isLoginInProcess'));

        const componentProps = useMemo(
            () => ({
                ...otherProps,
                tkPrefix: tkPrefix || portletTkPrefix
            }),
            [otherProps, portletTkPrefix]
        );

        // setShowWrapper or setWrapperClassNames are deprecated and will be removed soon
        const componentMethods = useMemo(
            () => ({
                isComponentMounted,
                setIsPortletShown: setShowWrapper,
                showComponentError: setComponentError,
                setPortletClassNames: setWrapperClassNames,
                showComponentMessage: setComponentMessage
            }),
            [setShowWrapper, setWrapperClassNames, setComponentError, setComponentMessage, isComponentMounted]
        );

        const componentConfig = useMemo(
            () => helpers.parseComponentConfig(formFactor, portletPreferences || config, parseConfig),
            [config, formFactor, portletPreferences]
        );

        if (componentError) {
            return <ErrorMessage error={componentError} className={style.messageContainer} />;
        }

        if (!isLoggedIn && isForLoggedIn || isLoginInProcess && isForLoggedIn) {
            return <div className={style.messageContainer}>{translate('notLoggedIn')}</div>;
        }

        if (isLoggedIn && isForLoggedOut) {
            return <div className={style.messageContainer}>{translate('isLoggedIn')}</div>;
        }

        if (componentMessage) {
            return <div className={style.messageContainer}>{componentMessage}</div>;
        }

        return (
            <ComponentPropsContext.Provider value={componentProps}>
                <ComponentConfigContext.Provider value={componentConfig}>
                    <ComponentMethodsContext.Provider value={componentMethods}>
                        <WrappedComponent {...props as P & Types.IWithComponentPortletProps} />
                    </ComponentMethodsContext.Provider>
                </ComponentConfigContext.Provider>
            </ComponentPropsContext.Provider>
        );
    };
